import React, { useCallback, useState } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { usePlaceSearch, type PlaceSuggestion } from "../../api/place";
import { Icon } from "../Icon/Icon";
import { colors } from "../../styles/colors";

/**
 * AddressAutocomplete — le champ d'adresse unique du back-office.
 *
 * CE QU'IL REMPLACE
 * -----------------
 * Rien. C'est precisement le probleme qu'il corrige : le back-office etait le
 * SEUL client de la plateforme sans recherche d'adresse. L'operateur tapait
 * latitude et longitude a la main, dans les deux formulaires qui creent des
 * donnees geographiques :
 *
 *   - ModalAddSign, qui cree les lignes `sign` que TOUTES les recherches de la
 *     plateforme lisent ensuite. Une coordonnee fausse saisie ici se propage
 *     partout et rien en aval ne peut la rattraper.
 *   - ModalAddPub, dont depend le ciblage publicitaire par rayon de 50 km.
 *
 * L'ORDRE EST CELUI DU SERVEUR
 * ----------------------------
 * Repertoire interne d'abord, fournisseur externe en repli, persistance a la
 * selection. Le composant ne re-decide rien : il affiche ce que /place/search
 * renvoie, dans l'ordre recu. C'est ce qui evite une quatrieme implementation
 * de la meme regle metier.
 *
 * IL RETOURNE TOUJOURS DES COORDONNEES OU RIEN
 * --------------------------------------------
 * `onSelect` recoit soit un lieu avec lat/lng non nulles, soit `null`. Jamais
 * un lieu a moitie rempli, et JAMAIS (0, 0) : voir la note dans api/place.ts
 * sur le `parseFloat(...) || 0` qui envoyait les enseignes dans le golfe de
 * Guinee.
 */

export interface ResolvedAddress {
  /** Libelle complet, tel qu'affiche et enregistre. */
  raw: string;
  lat: number;
  lng: number;
  /** Id de la ligne `address` deja existante, quand le lieu est interne. */
  address_id?: number | null;
  city?: string | null;
  phone?: string | null;
  centre_code?: string | null;
}

interface Props {
  label: string;
  /** Texte affiche dans le champ. Appartient au parent. */
  value: string;
  /**
   * Un lieu a ete choisi (coordonnees garanties), ou le champ a ete vide.
   *
   * `null` signifie \u00ab plus d'adresse \u00bb, pas \u00ab adresse sans position \u00bb : le
   * parent doit alors remettre lat/lng a null, pas a 0.
   */
  onSelect: (address: ResolvedAddress | null) => void;
  /**
   * Texte libre saisi, sans selection. Optionnel : certains formulaires
   * acceptent une adresse non geocodee, d'autres l'exigent.
   */
  onFreeText?: (text: string) => void;
  placeholder?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Position de reference, pour classer les resultats par proximite. */
  coords?: { lat: number; lng: number } | null;
}

export const AddressAutocomplete: React.FC<Props> = ({
  label,
  value,
  onSelect,
  onFreeText,
  placeholder,
  helperText,
  error,
  required = false,
  disabled = false,
  coords = null,
}) => {
  const { t } = useTranslation();
  const { suggestions, attribution, isSearching, isResolving, search, resolve, clear } =
    usePlaceSearch(coords);

  const [selected, setSelected] = useState<PlaceSuggestion | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const handleInputChange = useCallback(
    (_: unknown, next: string, reason: string) => {
      // `reason === 'reset'` est emis par MUI quand NOUS ecrivons la valeur
      // (selection, reinitialisation du parent). Relancer une recherche dessus
      // rouvrirait la liste juste apres que l'operateur a choisi.
      if (reason === "reset") return;

      onFreeText?.(next);
      setResolveError(null);
      search(next);

      if (!next.trim()) {
        setSelected(null);
        onSelect(null);
      }
    },
    [search, onSelect, onFreeText]
  );

  const handleChange = useCallback(
    async (_: unknown, place: PlaceSuggestion | null) => {
      setResolveError(null);

      if (!place) {
        setSelected(null);
        clear();
        onSelect(null);
        return;
      }

      setSelected(place);

      const resolved = await resolve(place);

      /*
       * Un lieu sans coordonnees exploitables est REFUSE, pas accepte a moitie.
       *
       * Accepter le libelle sans la position redonnerait exactement l'ancien
       * comportement : une enseigne enregistree sans point, donc absente de la
       * carte et de toute recherche par proximite, sans que rien ne l'ait
       * signale a l'operateur au moment ou il pouvait encore corriger.
       */
      if (!resolved || typeof resolved.lat !== "number" || typeof resolved.lng !== "number") {
        setResolveError(
          t(
            "address.resolveFailed",
            "La position de ce lieu n'a pas pu être récupérée. Choisissez une autre suggestion ou saisissez les coordonnées manuellement."
          )
        );
        onSelect(null);
        return;
      }

      setSelected(resolved);
      clear();

      onSelect({
        raw: resolved.raw ?? resolved.name ?? "",
        lat: resolved.lat,
        lng: resolved.lng,
        address_id: resolved.address_id ?? null,
        phone: resolved.phone ?? null,
        centre_code: resolved.centre_code ?? null,
      });
    },
    [resolve, clear, onSelect, t]
  );

  const busy = isSearching || isResolving;
  const displayedError = error || resolveError;

  return (
    <Box>
      <Autocomplete<PlaceSuggestion, false, false, true>
        freeSolo
        disabled={disabled}
        options={suggestions}
        filterOptions={(x) => x /* le serveur a deja filtre et classe */}
        value={selected}
        inputValue={value}
        onInputChange={handleInputChange}
        onChange={(e, v) => {
          // freeSolo renvoie une chaine quand l'operateur valide son texte sans
          // choisir de suggestion : ce n'est pas un lieu, donc pas de position.
          if (typeof v === "string") return;
          void handleChange(e, v);
        }}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.raw ?? option.name ?? ""
        }
        isOptionEqualToValue={(a, b) => a.id === b.id}
        groupBy={(option) =>
          option.source === "internal"
            ? t("address.internalGroup", "Déjà dans le répertoire")
            : t("address.externalGroup", "Suggestions")
        }
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box display="flex" alignItems="center" gap={1.25} width="100%">
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  flexShrink: 0,
                  backgroundColor:
                    option.source === "internal" ? colors.lightBlue : colors.default,
                }}
              >
                <Icon
                  name="map-pin"
                  width="15px"
                  height="15px"
                  color={option.source === "internal" ? colors.darkBlue : colors.greydark}
                />
              </Box>

              <Box minWidth={0} flex={1}>
                <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
                  {option.name}
                </Typography>
                <Typography noWrap sx={{ fontSize: 12, color: colors.greydark }}>
                  {option.secondary ?? option.raw ?? ""}
                </Typography>
              </Box>

              {typeof option.distance_km === "number" && (
                <Typography sx={{ fontSize: 11, color: colors.greydark, flexShrink: 0 }}>
                  {option.distance_km} km
                </Typography>
              )}
            </Box>
          </li>
        )}
        noOptionsText={t("address.noResults", "Aucun lieu trouvé")}
        loading={busy}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={!!displayedError}
            helperText={displayedError || helperText}
            placeholder={placeholder}
            size="small"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Icon name="search" width="16px" height="16px" color={colors.greydark} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {busy ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Credit du fournisseur : impose par ses conditions d'utilisation des
          que ses resultats sont affiches, c'est-a-dire exactement quand le
          serveur renvoie une attribution non nulle. Rendu verbatim. */}
      {attribution && (
        <Typography sx={{ mt: 0.5, fontSize: 11, color: colors.greydark, textAlign: "right" }}>
          {attribution}
        </Typography>
      )}
    </Box>
  );
};

export default AddressAutocomplete;
